import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
	AddressInput,
	CitySelectInput,
	CompanyNameInput,
	FirstNameInput,
	LastNameInput,
	PhoneNumberInput,
	PostalCodeInput,
	SelectInput,
	StateSelectInput,
	useBrandDetails,
} from 'dw-au-react-components';
import useForm from 'react-hook-form';
import { usePostalCodeData } from 'dw-au-react-components';
import DeliveryInstructionsInput from '../AddressForm/DeliveryInstructionsInput';

const StyledShippingForm = styled.form`
	.col-2 {
		flex-basis: 48%;
	}

	.select .dropdown {
		z-index: 9 !important;
	}
`;

function ShippingForm({
	defaultValues = '',
	handleFormSubmission,
	setStep,
	backToStep = '',
}) {
	const {
		register,
		errors,
		handleSubmit,
		watch,
		setValue,
		setError,
		clearError,
		reset,
		unregister,
	} = useForm({
		mode: 'onBlur',
		defaultValues,
	});

	const brandData = useBrandDetails();
	const [selectedValue, setSelectedValue] = useState('');
	const watchedPostalCode = watch('postalCode');
	const watchedCity = watch('city');
	const watchedState = watch('state');
	const [postalCode, setPostalCode] = useState('');
	const [cityList, setCityList] = useState([]);
	const [stateList, setStateList] = useState([]);
	const [addressFormError, setAddressFormError] = useState(false);

	useEffect(() => {
		if (defaultValues) {
			setSelectedValue(defaultValues.salutation);
			setValue('salutation', defaultValues.salutation);
			setValue('phoneNumber', defaultValues?.dayPhoneNumber);
		}
	}, [defaultValues]);

	const handleSelectChange = (itemObject) => {
		if (itemObject.value) {
			setSelectedValue(itemObject.value);
			setValue('salutation', itemObject.value);
		}
	};

	useEffect(() => {
		if (watchedPostalCode) {
			setPostalCode(watchedPostalCode);
		}
	}, [watchedPostalCode]);

	let postalCodeData = usePostalCodeData(postalCode, brandData.country);

	const { finalPostalCode, finalCityList, finalStateList, error, isLoading } =
		postalCodeData;

	let invalidPostalCode =
		postalCode.length && error && finalPostalCode === false;

	useEffect(() => {
		if (!isLoading && invalidPostalCode) {
			setError(
				'postalCode',
				'isValidPostalCode',
				'Please enter a valid post code'
			);
			setAddressFormError('Please enter a valid post code');
			setCityList([]);
			setStateList([]);
			setValue('city', '');
			setValue('state', '');
		} else {
			clearError('postalCode');
			setAddressFormError(false);
		}
	}, [invalidPostalCode]);

	useEffect(() => {
		let cityOptions = '';
		if (finalPostalCode && finalCityList.length > 0) {
			cityOptions = finalCityList.map((city) => {
				return {
					option: city,
					value: city,
				};
			});
			setCityList(cityOptions);
		}
	}, [finalCityList]);

	useEffect(() => {
		let cityOptions = '';
		if (finalPostalCode && finalStateList.length > 0) {
			cityOptions = finalStateList.map((city) => {
				return {
					option: city.stateCode,
					value: city.stateCode,
				};
			});
			setStateList(cityOptions);
			setValue('stateCode', cityOptions[0].value);
		}
	}, [finalStateList]);

	return (
		<StyledShippingForm
			className='m-0'
			onSubmit={handleSubmit((data) => handleFormSubmission(data))}
		>
			<div className='mb-16'>
				<SelectInput
					optionsList={[
						{
							option: 'Mr.',
							value: 'Mr.',
						},
						{
							option: 'Mrs.',
							value: 'Mrs.',
						},
						{
							option: 'Ms.',
							value: 'Ms.',
						},
						{
							option: 'Miss',
							value: 'Miss',
						},
						{
							option: 'Dr',
							value: 'Dr',
						},
						{
							option: 'Prof',
							value: 'Prof',
						},
					]}
					label='Title*'
					register={register({ name: 'salutation' })}
					errors={errors}
					onChange={handleSelectChange}
					value={selectedValue}
				/>
			</div>

			<div className='mb-16'>
				<FirstNameInput
					register={register}
					errors={errors}
					label='First Name*'
					placeholder='First Name*'
				/>
			</div>

			<div className='mb-16'>
				<LastNameInput register={register} errors={errors} />
			</div>

			<div className='mb-16'>
				<CompanyNameInput register={register} />
			</div>

			<div className='mb-16'>
				<PhoneNumberInput
					register={register}
					errors={errors}
					country={brandData.country}
				/>
			</div>

			<div className='mb-16 subtitle lower roboto'>Shipping information</div>

			<div className='mb-16'>
				<AddressInput
					register={register}
					errors={errors}
					country={brandData.country}
				/>
			</div>

			{brandData.country !== 'NZ' && (
				<div className='mb-16'>
					<AddressInput
						type={2}
						required={false}
						register={register}
						errors={errors}
						country={brandData.country}
					/>
				</div>
			)}

			<div className='mb-16'>
				<PostalCodeInput register={register} errors={errors} />
			</div>

			<div
				className={`${
					brandData.country !== 'NZ' ? 'd-flex justify-content-between' : ''
				}`}
			>
				<div className={`mb-16 ${brandData.country !== 'NZ' ? 'col-2' : ''}`}>
					<div className='section form-row'>
						<CitySelectInput
							register={register}
							errors={errors}
							cityList={cityList}
							setValue={setValue}
							clearError={clearError}
							defaultValue={watchedCity}
						/>
					</div>
				</div>

				<div className={`mb-16 ${brandData.country !== 'NZ' ? 'col-2' : ''}`}>
					{brandData.country !== 'NZ' ? (
						<div className='section form-row'>
							<StateSelectInput
								register={register}
								errors={errors}
								stateList={stateList}
								setValue={setValue}
								clearError={clearError}
								defaultValue={watchedState}
							/>
							<input
								type='hidden'
								name='stateCode'
								/* eslint-disable react/no-unknown-property */
								register={register({ name: 'stateCode' })}
							/>
						</div>
					) : null}
				</div>
			</div>

			<div className='mb-16'>
				<DeliveryInstructionsInput
					defaultValue={
						defaultValues.deliveryInstructionTxt
							? defaultValues.deliveryInstructionTxt
							: defaultValues.deliveryInstruction
					}
					register={register}
					errors={errors}
					setValue={setValue}
					clearError={clearError}
					unregister={unregister}
				/>
			</div>

			<button
				type='submit'
				className={'btn btn-primary font-weight-bold dmlp__btn w-100 mb-20'}
			>
				Save
			</button>
			<div
				className='btn btn-outline-secondary btn-block'
				onClick={() => {
					reset({});
					if (backToStep) {
						setStep(backToStep);
					} else {
						setStep(6);
					}
				}}
			>
				Cancel
			</div>

			{addressFormError && (
				<div className='mt-20 alert alert-danger'>{addressFormError}</div>
			)}
		</StyledShippingForm>
	);
}

export default ShippingForm;
